import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Camera, X, Upload, Loader, CheckCircle, Edit2, Trash2, Receipt, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from '../i18n/LanguageContext';

function ReceiptScanner({ onItemsAdded, onClose }) {
  const { t } = useTranslation();
  const MAX_IMAGE_DIMENSION = 1600;
  const JPEG_QUALITY = 0.72;
  const [step, setStep] = useState('capture');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preparingPhotos, setPreparingPhotos] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [editingArticles, setEditingArticles] = useState([]);
  const [magasin, setMagasin] = useState('');
  const [dateAchat, setDateAchat] = useState(new Date().toISOString().split('T')[0]);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const pollingRef = useRef(null);
  const timeRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timeRef.current) clearInterval(timeRef.current);
    };
  }, []);

  const loadImageFromFile = useCallback((file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  }), []);

  const optimizeImageFile = useCallback(async (file) => {
    const image = await loadImageFromFile(file);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Impossible d\'optimiser l\'image');
    }

    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    const originalSize = file.size || 0;
    const compressedSize = Math.floor((dataUrl.length * 3) / 4);

    return { dataUrl, originalSize, compressedSize, targetWidth, targetHeight };
  }, [JPEG_QUALITY, MAX_IMAGE_DIMENSION, loadImageFromFile]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 3) {
      toast.error(t('receipt.maxPhotos'));
      e.target.value = '';
      return;
    }

    setPreparingPhotos(true);
    let totalOriginal = 0;
    let totalCompressed = 0;

    try {
      const optimizedPhotos = [];
      for (const file of files) {
        try {
          const optimized = await optimizeImageFile(file);
          totalOriginal += optimized.originalSize;
          totalCompressed += optimized.compressedSize;
          optimizedPhotos.push({
            id: Date.now() + Math.random(),
            dataUrl: optimized.dataUrl,
            file,
            optimized
          });
        } catch (error) {
          console.error('Erreur optimisation image:', error);
          toast.error(`Image ignorée: ${file.name}`);
        }
      }

      if (optimizedPhotos.length > 0) {
        setPhotos(prev => [...prev, ...optimizedPhotos]);

        if (totalOriginal > 0 && totalCompressed > 0 && totalCompressed < totalOriginal) {
          const savedPct = Math.round(((totalOriginal - totalCompressed) / totalOriginal) * 100);
          toast.success(`Photos optimisées (-${savedPct}% de taille)`);
        }
      }
    } finally {
      setPreparingPhotos(false);
    }

    e.target.value = '';
  };

  const removePhoto = (photoId) => setPhotos(prev => prev.filter(p => p.id !== photoId));

  const getProgressMessage = useCallback((pct) => {
    if (pct < 20) return t('receipt.analyzeStep1');
    if (pct < 60) return t('receipt.analyzeStep2');
    if (pct < 80) return t('receipt.analyzeStep3');
    return t('receipt.analyzeComplete');
  }, [t]);

  const handleScanReceipt = async () => {
    if (photos.length === 0 || preparingPhotos) return;

    setLoading(true);
    setStep('processing');
    setElapsedTime(0);
    setProgress(5);
    setProgressMessage(t('receipt.analyzeStep1'));

    timeRef.current = setInterval(() => setElapsedTime(prev => prev + 1), 1000);

    try {
      const imagesBase64 = photos.map(p => p.dataUrl);

      // Submit async job with upload progress first
      const submitRes = await axios.post('/items/scan-receipt-async', {
        images: imagesBase64, magasin, date_achat: dateAchat
      }, {
        onUploadProgress: (event) => {
          if (!event.total) return;
          const uploadPct = Math.max(5, Math.min(25, Math.round((event.loaded / event.total) * 25)));
          setProgress(uploadPct);
          setProgressMessage('Envoi des photos...');
        }
      });

      if (!submitRes.data.success) {
        throw new Error('Failed to submit scan job');
      }

      const jobId = submitRes.data.job_id;
      setProgress(10);
      setProgressMessage('Analyse en cours...');

      // Poll for results
      await new Promise((resolve, reject) => {
        pollingRef.current = setInterval(async () => {
          try {
            const statusRes = await axios.get(`/items/scan-receipt-status/${jobId}`);
            const { status, progress: jobProgress, result, error } = statusRes.data;

            setProgress(jobProgress || 10);
            setProgressMessage(getProgressMessage(jobProgress || 10));

            if (status === 'completed') {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              setScannedData(result);
              setEditingArticles(result.articles.map((article, index) => ({
                ...article, id: index, editing: false
              })));
              setMagasin(result.magasin || magasin);
              setDateAchat(result.date_achat || dateAchat);
              setStep('review');
              toast.success(`${result.nb_articles} ${t('receipt.detected')}`);
              resolve();
            } else if (status === 'error') {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              reject(new Error(error || 'Erreur lors de l\'analyse. Reessayez avec une photo plus nette.'));
            }
          } catch (err) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            reject(err);
          }
        }, 2000);
      });
    } catch (error) {
      console.error('Erreur scan ticket:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error';
      toast.error(errorMsg);
      setStep('capture');
    } finally {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (timeRef.current) clearInterval(timeRef.current);
      setLoading(false);
    }
  };

  const handleEditArticle = (index, field, value) => {
    setEditingArticles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const toggleEdit = (index) => {
    setEditingArticles(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], editing: !updated[index].editing };
      return updated;
    });
  };

  const removeArticle = (index) => {
    setEditingArticles(prev => prev.filter((_, i) => i !== index));
  };

  const handleConfirmAndAdd = async () => {
    if (editingArticles.length === 0) return;

    const missingPriceItem = editingArticles.find((article) => {
      const numericPrice = Number(article.prix);
      return !Number.isFinite(numericPrice) || numericPrice <= 0;
    });

    if (missingPriceItem) {
      toast.error(`Prix obligatoire pour l'article: ${missingPriceItem.nom || 'Inconnu'}`);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/items/add-receipt-items', {
        articles: editingArticles, magasin, date_achat: dateAchat
      });
      if (response.data.success) {
        toast.success(response.data.message);
        onItemsAdded?.();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4" onClick={onClose}>
      <Card className="w-full max-w-2xl mx-2 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="receipt-scanner-modal">
        <CardHeader className="p-3 sm:p-6 pb-2 sm:pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Receipt className="w-5 h-5 text-green-600" />
              {t('receipt.title')}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="close-receipt-scanner">
              <X size={16} />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          {/* STEP 1: CAPTURE */}
          {step === 'capture' && (
            <div className="space-y-4">
              {/* Photo previews */}
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((photo) => (
                    <div key={photo.id} className="relative aspect-[3/4] rounded-lg overflow-hidden border">
                      <img src={photo.dataUrl} alt="Ticket" className="w-full h-full object-cover" />
                      <Button variant="destructive" size="sm" className="absolute top-1 right-1 h-6 w-6 p-0" onClick={() => removePhoto(photo.id)}>
                        <X size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Optional store/date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">{t('receipt.store')}</Label>
                  <Select value={magasin || "non_renseigne"} onValueChange={(val) => setMagasin(val === "non_renseigne" ? "" : val)}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non_renseigne">-</SelectItem>
                      <SelectItem value="Carrefour">Carrefour</SelectItem>
                      <SelectItem value="Lidl">Lidl</SelectItem>
                      <SelectItem value="Auchan">Auchan</SelectItem>
                      <SelectItem value="Intermarché">Intermarché</SelectItem>
                      <SelectItem value="Leclerc">Leclerc</SelectItem>
                      <SelectItem value="Casino">Casino</SelectItem>
                      <SelectItem value="Monoprix">Monoprix</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs sm:text-sm">{t('receipt.date')}</Label>
                  <Input type="date" value={dateAchat} onChange={(e) => setDateAchat(e.target.value)} className="h-9 text-sm" />
                </div>
              </div>

              {/* Capture buttons */}
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => cameraInputRef.current?.click()} disabled={photos.length >= 3 || preparingPhotos} className="w-full h-10 text-sm">
                  <Camera size={16} className="mr-2" /> {t('receipt.takePhoto')}
                </Button>
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={photos.length >= 3 || preparingPhotos} className="w-full h-10 text-sm">
                  <Upload size={16} className="mr-2" /> {t('receipt.addPhotos')}
                </Button>
              </div>
              <p className="text-xs text-center text-gray-500">{t('receipt.maxPhotos')} ({photos.length}/3)</p>
              {preparingPhotos && (
                <p className="text-xs text-center text-blue-600">Optimisation des photos en cours...</p>
              )}

              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />

              {photos.length > 0 && (
                <Button onClick={handleScanReceipt} disabled={loading || preparingPhotos} className="w-full bg-green-600 hover:bg-green-700 text-white h-11 text-sm font-semibold" data-testid="analyze-receipt-button">
                  <CheckCircle size={16} className="mr-2" /> {t('receipt.analyze')}
                </Button>
              )}
            </div>
          )}

          {/* STEP 2: PROCESSING */}
          {step === 'processing' && (
            <div className="text-center py-6 sm:py-10">
              <div className="relative inline-block mb-4">
                <Loader size={56} className="animate-spin text-green-600" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold text-green-700">{elapsedTime}s</span>
                </div>
              </div>
              <p className="text-sm sm:text-base font-semibold text-gray-800 mb-3">{progressMessage}</p>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3 overflow-hidden">
                <div className="bg-green-500 h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-gray-500">{progress}% - {photos.length} photo(s)</p>
            </div>
          )}

          {/* STEP 3: REVIEW */}
          {step === 'review' && (
            <div className="space-y-3">
              <div className="bg-green-50 p-2.5 rounded-lg border border-green-200">
                <p className="text-sm text-green-800 font-medium">{editingArticles.length} {t('receipt.detected')}</p>
              </div>

              <div className="grid grid-cols-3 gap-2 p-2.5 bg-gray-50 rounded-lg text-xs sm:text-sm">
                <div><p className="text-gray-500">{t('receipt.store')}</p><p className="font-medium truncate">{magasin || '-'}</p></div>
                <div><p className="text-gray-500">{t('receipt.date')}</p><p className="font-medium">{dateAchat}</p></div>
                <div><p className="text-gray-500">{t('receipt.total')}</p><p className="font-medium">{scannedData?.total ? `${scannedData.total.toFixed(2)} €` : 'N/A'}</p></div>
              </div>

              <div className="max-h-[45vh] overflow-y-auto space-y-2 -mx-1 px-1">
                {editingArticles.map((article, index) => (
                  <Card key={article.id} className="p-2.5 sm:p-3">
                    {!article.editing ? (
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{article.nom}</p>
                          <p className="text-xs text-gray-600">
                            {article.quantite} {article.unite}
                            {article.prix != null && ` · ${article.prix.toFixed(2)} €`}
                            {' · '}{article.categorie}
                          </p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => toggleEdit(index)} className="h-7 w-7 p-0"><Edit2 size={13} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => removeArticle(index)} className="h-7 w-7 p-0 text-red-600"><Trash2 size={13} /></Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Input placeholder={t('form.name')} value={article.nom} onChange={(e) => handleEditArticle(index, 'nom', e.target.value)} className="text-sm h-8" />
                        <div className="grid grid-cols-3 gap-1.5">
                          <Input type="number" inputMode="decimal" placeholder={t('form.quantity')} value={article.quantite} onChange={(e) => handleEditArticle(index, 'quantite', parseFloat(e.target.value) || 0)} className="text-sm h-8" />
                          <Select value={article.unite} onValueChange={(val) => handleEditArticle(index, 'unite', val)}>
                            <SelectTrigger className="text-sm h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pièces">pièces</SelectItem>
                              <SelectItem value="kg">kg</SelectItem>
                              <SelectItem value="g">g</SelectItem>
                              <SelectItem value="L">L</SelectItem>
                              <SelectItem value="mL">mL</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input type="number" inputMode="decimal" step="0.01" placeholder="Prix" value={article.prix || ''} onChange={(e) => handleEditArticle(index, 'prix', parseFloat(e.target.value) || 0)} className="text-sm h-8" />
                        </div>
                        <Button variant="outline" size="sm" onClick={() => toggleEdit(index)} className="w-full h-7 text-xs">
                          <CheckCircle size={12} className="mr-1" /> OK
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => { setStep('capture'); setEditingArticles([]); }}
                  className="flex-1 h-10 text-sm font-semibold border-slate-400 bg-slate-100 text-slate-900 hover:bg-slate-200"
                >
                  <ArrowLeft size={14} className="mr-1" /> {t('form.cancel')}
                </Button>
                <Button onClick={handleConfirmAndAdd} disabled={loading || editingArticles.length === 0} className="flex-1 bg-green-600 hover:bg-green-700 text-white h-10 text-sm font-semibold" data-testid="confirm-receipt-items">
                  {loading ? <Loader size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
                  {t('receipt.addAll')} ({editingArticles.length})
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ReceiptScanner;
